import { normalizeCredentialFields, hasEffectiveCredentialValue } from './credentialFields';
import { buildGatewayRateRules, costingRateKey } from './paymentGatewayPayload';

export const PAYMENT_API_FIELDS = ['merchant_id', 'api_key', 'secret_key'];
export const PAYMENT_BO_FIELDS = ['bo_username', 'bo_password'];
export const PAYMENT_CALLBACKS = [{ name: 'Callback', path: '' }];

export const validatePaymentGatewayConfiguration = (provider, canEditFinancial) => {
  const validRate = rate => ['number', 'string'].includes(typeof rate) && String(rate).trim() !== '' && Number.isFinite(Number(rate)) && Number(rate) >= 0 && Number(rate) <= 100;
  if (canEditFinancial) {
    if (Array.isArray(provider.selling_rate_rules)) {
      const expected = buildGatewayRateRules(provider.provider_id || {});
      const keys = new Set(provider.selling_rate_rules.map(costingRateKey));
      if (expected.length !== provider.selling_rate_rules.length || keys.size !== expected.length || expected.some(rule => !keys.has(costingRateKey(rule)))) return 'Set a selling rate for every category, country and currency combination.';
      if (provider.selling_rate_rules.some(rule => !validRate(rule.rate) || !validRate(rule.out_rate === undefined ? rule.rate : rule.out_rate))) return 'Selling rate must be between 0 and 100.';
      const validFixed = value => ['number', 'string'].includes(typeof value) && String(value).trim() !== '' && Number.isFinite(Number(value)) && Number(value) >= 0;
      if (provider.selling_rate_rules.some(rule => ['in_fixed_rate', 'out_fixed_rate'].some(key => !validFixed(rule[key] === undefined ? 0 : rule[key])))) return 'Fixed selling rate must be a non-negative number.';
    } else if (!validRate(provider.selling_rate)) return 'Selling rate must be between 0 and 100.';
  }
  const validUrl = value => {
    try { const url = new URL(value); return ['http:', 'https:'].includes(url.protocol) && !!url.hostname; } catch { return false; }
  };
  if ((provider.group_links || []).some(link => link.url?.trim() && !validUrl(link.url.trim()))) return 'Group links must have a valid HTTP or HTTPS URL.';
  for (const env of ['production', 'staging']) {
    for (const key of ['bo_urls', 'api_urls']) {
      if ((provider.credentials?.[env]?.[key] || []).some(url => url.trim() && !validUrl(url.trim()))) return `${env === 'production' ? 'Production' : 'Staging'} URLs must use valid HTTP or HTTPS addresses.`;
    }
  }
  return '';
};

const normalizeEnvironment = value => {
  const input = value || {};
  const urls = key => {
    const values = Array.isArray(input[key]) ? input[key] : input[key.slice(0, -1)] ? [input[key.slice(0, -1)]] : [];
    return values.length ? values : [''];
  };
  return { ...input, bo_urls: urls('bo_urls'), api_urls: urls('api_urls'), callback_paths: input.callback_paths || {} };
};

export const normalizeCredentials = input => ({
  production: normalizeEnvironment(input?.production), staging: normalizeEnvironment(input?.staging)
});

export const resolveProviderStatus = provider => provider?.status || (provider?.is_active === true ? 'active' : 'draft');

export const isEnvComplete = (env, keys = [], callbacks = []) => {
  if (!env) return false;
  for (const key of ['bo_urls', 'api_urls']) {
    if (!env[key]?.length || env[key].some(url => !url || !String(url).trim())) return false;
  }
  if (normalizeCredentialFields(keys).some(field => !hasEffectiveCredentialValue(env, field))) return false;
  return callbacks.every(callback => String(env.callback_paths?.[callback.name] || callback.path || '').trim());
};

export const paymentGatewayConfiguration = item => ({
  ...item,
  paymentGateway: true,
  description: item.description || '',
  documents: (item.documents || []).map(document => ({ ...document })),
  name: item.provider_id?.provider_id?.name || item.provider_id?.code || 'Payment Gateway',
  source: item.provider_id?.source?.name || '—',
  api_credential_keys: PAYMENT_API_FIELDS,
  bo_credential_keys: PAYMENT_BO_FIELDS,
  callback_urls: PAYMENT_CALLBACKS,
  master_provider_group_links: item.provider_id?.group_links || [],
  group_links: item.group_links ?? item.provider_id?.group_links ?? [],
  credentials: normalizeCredentials(item.credentials),
  selling_rate: item.selling_rate ?? 0,
  selling_rate_rules: buildGatewayRateRules(item.provider_id || {}, item.selling_rate_rules, item.selling_rate ?? 0),
  status: resolveProviderStatus(item)
});
